import { Injectable, inject, signal, computed } from '@angular/core';
import { EmpleadoRepository } from 'src/app/domain/service/empleado/empleado-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { EmpleadoFiltro, EmpleadoResult, EmpleadoUenFiltro } from 'src/app/domain/model/empleado/empleado';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';

/**
 * Facade del listado de "Administrar Empleados/Consultores" (HU1259115).
 *
 * <p>Maneja el estado de la búsqueda por UEN de RN-01, la paginación cursor de
 * servidor (keyset, ver acceso-datos-oracle §7), y expone únicamente estado
 * (signals) + métodos al container — sin lógica de presentación (patrón
 * Facade + Container/Dumb, frontend-girop §14).</p>
 *
 * <p>Legacy: AdminEmpleadosActivity → AdminEmpleadosHandler.consultar()/
 * listarEmpleadosXUen(). El legado no tiene un selector de "modo" explícito:
 * el campo Unidad Administrativa siempre está visible junto a los demás
 * filtros, nunca es obligatorio, y seleccionar una UEN por la lupa
 * (`seleccionaronItem()`, `AdminEmpleadosViewImpl.java`) activa implícitamente
 * el flag `buscarPorUen` — los filtros de identificación se siguen aplicando
 * igual. `buscandoPorUen` reproduce ese mismo flag derivándolo de si hay una
 * UEN seleccionada, sin un toggle manual (fase 12, corrección de un
 * comportamiento inventado: radio "Buscar por" + validación de obligatoriedad
 * que no existen en el legado).</p>
 */
@Injectable()
export class AdministrarEmpleadosFacade {

  private readonly empleadoRepository = inject(EmpleadoRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);

  // ==================== Filtros ====================

  /**
   * Legacy: `AdminEmpleadosViewImpl.setDatosListas()` carga el combo de Tipo
   * Identificación sin opción en blanco y con `Constants.LIST_VALUE_TYPE_ID_CC`
   * ("C", Cédula) preseleccionado — el filtro `tipoId=C` SIEMPRE se aplica en
   * el legado salvo que el usuario cambie el combo manualmente. Se replica
   * ese mismo default aquí para que el conteo de resultados coincida con el
   * legado quien no cambie el filtro (confirmado: sin este default, el
   * listado migrado trae ~7 empleados más que el legado — los que tienen un
   * tipo de identificación distinto de Cédula).
   *
   * <p>`activo: ''` (Todos): legado inicializa `rbgActivo` con la opción
   * "Todos" (`Constants.ALL_VALUE = null`) tanto al cargar la pantalla como
   * al hacer clic en "Limpiar" (`reset()`) — no filtra por activo por
   * defecto. `''` es el mismo valor que usa el radio "Todos" del template.</p>
   */
  filtro: EmpleadoFiltro = { activo: '', tipoId: 'C' };
  readonly nmconsecUen = signal<number | null>(null);
  readonly uenDisplay = signal<string>('');

  /** Legacy: flag `buscarPorUen`, activado solo al seleccionar una UEN (nunca por un toggle manual). */
  readonly buscandoPorUen = computed(() => this.nmconsecUen() !== null);

  readonly tiposIdentificacion = signal<CatalogoOption[]>([]);
  readonly tiposEmpleado = signal<CatalogoOption[]>([]);
  readonly oficinas = signal<CatalogoOption[]>([]);

  // ==================== Resultado / estado ====================

  readonly items = signal<EmpleadoResult[]>([]);
  readonly loading = signal(false);
  readonly busquedaRealizada = signal(false);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<'info' | 'warning' | 'error'>('info');

  // ==================== Paginación cursor (server-side) ====================

  readonly currentPage = signal(1);
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly totalElements = signal(0);
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;

  readonly totalPages = computed(() => Math.ceil(this.totalElements() / this.pageSize()) || 1);
  // frontend-girop §17.7: la navegación la habilitan los flags de la RESPUESTA del servidor.
  readonly hasPrevious = signal(false);
  readonly hasNext = signal(false);
  readonly registroInicio = computed(() => {
    if (this.totalElements() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });
  readonly registroFin = computed(() => Math.min(this.currentPage() * this.pageSize(), this.totalElements()));

  async cargarCatalogos(): Promise<void> {
    try {
      this.tiposIdentificacion.set(await this.catalogoRepository.getTiposIdentificacion());
    } catch (error) {
      console.error('Error cargando tipos de identificación:', error);
    }
    try {
      this.tiposEmpleado.set(await this.catalogoRepository.getTiposEmpleado());
    } catch (error) {
      console.error('Error cargando tipos de empleado:', error);
    }
    try {
      this.oficinas.set(await this.catalogoRepository.getOficinas());
    } catch (error) {
      console.error('Error cargando oficinas:', error);
    }
  }

  seleccionarUen(nmconsecutivo: number, display: string): void {
    this.nmconsecUen.set(nmconsecutivo);
    this.uenDisplay.set(display);
  }

  limpiarUen(): void {
    this.nmconsecUen.set(null);
    this.uenDisplay.set('');
  }

  limpiarFiltros(): void {
    this.filtro = { activo: '', tipoId: 'C' };
    this.limpiarUen();
    this.items.set([]);
    this.totalElements.set(0);
    this.busquedaRealizada.set(false);
    this.mensaje.set('');
    this.currentPage.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
  }

  async buscar(): Promise<void> {
    this.busquedaRealizada.set(true);
    await this.cargarPaginaActual(null, 'NEXT', 1);
  }

  irPrimeraPagina(): void {
    if (this.loading()) return;
    void this.cargarPaginaActual(null, 'NEXT', 1);
  }

  irUltimaPagina(): void {
    if (this.loading()) return;
    void this.cargarPaginaActual(null, 'LAST', this.totalPages());
  }

  irPaginaAnterior(): void {
    if (this.loading() || !this.hasPrevious()) return;
    void this.cargarPaginaActual(this.previousCursorActual, 'PREVIOUS', Math.max(1, this.currentPage() - 1));
  }

  irPaginaSiguiente(): void {
    if (this.loading() || !this.hasNext()) return;
    void this.cargarPaginaActual(this.nextCursorActual, 'NEXT', this.currentPage() + 1);
  }

  cambiarTamanioPagina(size: number): void {
    this.pageSize.set(size);
    void this.cargarPaginaActual(null, 'NEXT', 1);
  }

  /**
   * Pide una página al servidor. {@code pagina} solo alimenta las etiquetas y se aplica
   * únicamente tras una respuesta exitosa (frontend-girop §17.7).
   */
  private async cargarPaginaActual(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina: number
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');
    try {
      const size = this.pageSize();
      const response = this.buscandoPorUen()
        ? await this.empleadoRepository.buscarPorUen(this.buildUenFiltro(), size, cursor ?? undefined, direction)
        : await this.empleadoRepository.buscar(this.filtro, size, cursor ?? undefined, direction);

      this.items.set(response.content);
      this.totalElements.set(response.totalElements ?? response.content.length);
      this.nextCursorActual = response.nextCursor ?? null;
      this.previousCursorActual = response.previousCursor ?? null;
      this.hasNext.set(!!response.hasNext);
      this.hasPrevious.set(!!response.hasPrevious);
      const total = this.totalElements();
      this.currentPage.set(
        direction === 'LAST' ? (size > 0 && total > 0 ? Math.ceil(total / size) : 1) : pagina
      );

      if (response.content.length === 0) {
        // Legacy: cons.busquedaNoArrojoResultado() (WidgetsNamesConstants.properties).
        this.mensaje.set('No se recuperó información con los parámetros ingresados.');
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error buscando empleados:', error);
      // Legacy: cons.genericError() (WidgetsNamesConstants.properties).
      this.mensaje.set('Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.');
      this.mensajeTipo.set('error');
      this.items.set([]);
      this.totalElements.set(0);
      this.hasNext.set(false);
      this.hasPrevious.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  private buildUenFiltro(): EmpleadoUenFiltro {
    return { ...this.filtro, nmconsecUen: this.nmconsecUen()! };
  }
}
